import { useEffect, useState } from 'react';

export interface SharedSnapshotMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at: string | null;
}

export interface SharedConversation {
  title: string;
  shared_at: string;
  messages: SharedSnapshotMessage[];
}

interface State {
  data: SharedConversation | null;
  loading: boolean;
  /** 'not_found' = 404 (unknown / revoked). 'error' = network / 5xx. */
  error: null | 'not_found' | 'error';
}

const INITIAL: State = { data: null, loading: true, error: null };

/**
 * One-shot fetch of a publicly-shared conversation snapshot.
 *
 * Does NOT use the authenticated apiClient — public share pages must work for
 * anonymous viewers without a logged-in session. The endpoint is itself
 * rate-limited (60 req/min per IP) on the backend.
 */
export function useSharedConversation(token: string | undefined): State {
  const [state, setState] = useState<State>(INITIAL);

  useEffect(() => {
    if (!token) {
      setState({ data: null, loading: false, error: 'not_found' });
      return;
    }

    let cancelled = false;
    setState(INITIAL);

    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${baseUrl}/api/v1/shared/${encodeURIComponent(token)}`, {
      method: 'GET',
      // No Authorization header — endpoint is public.
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setState({ data: null, loading: false, error: 'not_found' });
          return;
        }
        if (!res.ok) {
          setState({ data: null, loading: false, error: 'error' });
          return;
        }
        const data = (await res.json()) as SharedConversation;
        setState({ data, loading: false, error: null });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: null, loading: false, error: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return state;
}
